import React, { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { ArrowRight, Clock, BookOpen, BarChart2, Tag } from "lucide-react";
import { useLang } from "../lib/i18n";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LEVEL_META = {
  beginner: { tr: "Başlangıç", en: "Beginner", color: "bg-sage-100 text-sage-700" },
  intermediate: { tr: "Orta Düzey", en: "Intermediate", color: "bg-bluefog-200 text-sage-800" },
  advanced: { tr: "İleri", en: "Advanced", color: "bg-terracotta-50 text-terracotta-500" },
};

function pickLang(field, lang, fallback = "") {
  if (!field) return fallback;
  if (typeof field === "string") return field;
  return field[lang] || field.tr || field.en || fallback;
}

function totalDuration(modules = []) {
  let seconds = 0;
  for (const module of modules) {
    for (const lesson of (module.lessons || [])) seconds += lesson.duration_sec || 0;
  }
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  return hours > 0 ? `${hours} sa ${minutes} dk` : `${minutes} dk`;
}

function totalLessons(modules = []) {
  return modules.reduce((total, module) => total + (module.lessons || []).length, 0);
}

export default function Trainings() {
  const { lang } = useLang();
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    axios.get(`${API}/courses`, { params: { lang } })
      .then(({ data }) => setCourses(data || []))
      .catch(() => setCourses([]))
      .finally(() => setLoading(false));
  }, [lang]);

  return (
    <div data-testid="trainings-page">
      <section className="container-x pt-12 pb-10 md:pt-20 md:pb-14">
        <span className="label-eyebrow">Online Eğitimler</span>
        <h1 className="mt-5 font-serif text-5xl md:text-7xl leading-[1.02] tracking-tight text-ink max-w-4xl">
          Kendi hızınızda, bilimden beslenen eğitimler.
        </h1>
        <p className="mt-6 text-lg md:text-xl text-ink/70 font-light leading-relaxed max-w-2xl">
          İş ve örgüt psikolojisinin kanıta dayalı içeriklerini, modüler ve video tabanlı eğitimlerle keşfedin.
        </p>
      </section>

      <section className="container-x pb-24 md:pb-32">
        {loading ? (
          <div className="text-ink/55 text-sm py-16 text-center">Yükleniyor…</div>
        ) : courses.length === 0 ? (
          <div className="surface-card p-12 text-center">
            <BookOpen className="w-8 h-8 mx-auto mb-3 text-ink/30" strokeWidth={1.4} />
            <p className="font-serif text-2xl text-ink mb-2">Yakında yeni eğitimler.</p>
            <p className="text-sm text-ink/60">İçeriklerimiz hazırlanıyor, takipte kalın.</p>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {courses.map((course) => <CourseCard key={course.id || course.slug} course={course} lang={lang} />)}
          </div>
        )}
      </section>
    </div>
  );
}

function CourseCard({ course, lang }) {
  const level = LEVEL_META[course.level] || null;
  const duration = totalDuration(course.modules || []);
  const lessons = totalLessons(course.modules || []);
  const price = course.price_kurus > 0 ? `${(course.price_kurus / 100).toLocaleString("tr-TR")} TL` : "Ücretsiz";

  return (
    <Link to={`/trainings/${course.slug}`} data-testid={`course-card-${course.slug}`} className="group surface-card overflow-hidden flex flex-col hover:-translate-y-1 hover:shadow-float transition-all duration-500">
      <div className="aspect-[16/9] overflow-hidden bg-cream-200 relative">
        {course.thumbnail ? <img src={course.thumbnail} alt="" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" /> : <div className="w-full h-full flex items-center justify-center text-cream-300"><BookOpen className="w-12 h-12" strokeWidth={1} /></div>}
        {level && <span className={`absolute top-3 left-3 text-[10px] uppercase tracking-[0.18em] font-medium px-2.5 py-1 rounded-full ${level.color}`}>{level[lang] || level.tr}</span>}
        {course.is_last_seats && <span className="absolute top-3 right-3 text-[10px] uppercase tracking-[0.14em] font-medium px-2.5 py-1 rounded-full bg-red-600 text-white">Son {course.remaining_capacity} koltuk</span>}
        {course.is_sold_out && <span className="absolute top-3 right-3 text-[10px] uppercase tracking-[0.14em] font-medium px-2.5 py-1 rounded-full bg-ink/75 text-white">Kontenjan doldu</span>}
      </div>

      <div className="p-6 flex flex-col flex-1">
        <h3 className="font-serif text-2xl text-ink leading-tight">{pickLang(course.title, lang)}</h3>
        <p className="mt-3 text-sm font-light text-ink/65 leading-relaxed line-clamp-3">{pickLang(course.description, lang)}</p>

        <div className="mt-4 flex flex-wrap gap-x-4 gap-y-1 text-xs text-ink/55">
          {duration !== "0 dk" && <span className="inline-flex items-center gap-1"><Clock className="w-3 h-3" strokeWidth={1.5} />{duration}</span>}
          {lessons > 0 && <span className="inline-flex items-center gap-1"><BookOpen className="w-3 h-3" strokeWidth={1.5} />{lessons} ders</span>}
          {course.access_days > 0 && <span className="inline-flex items-center gap-1"><BarChart2 className="w-3 h-3" strokeWidth={1.5} />{course.access_days} günlük erişim</span>}
          {course.delivery_mode === "offline" && <span className="inline-flex items-center gap-1 text-terracotta-500">Yüz yüze</span>}
        </div>

        {(course.tags || []).length > 0 && <div className="mt-3 flex flex-wrap gap-1.5">{course.tags.slice(0, 3).map((tag) => <span key={tag} className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.15em] bg-cream-200 text-ink/60 px-2 py-0.5 rounded-full"><Tag className="w-2.5 h-2.5" strokeWidth={1.5} />{tag}</span>)}</div>}

        <div className="mt-auto pt-5 flex items-center justify-between">
          <span className="font-serif text-xl text-terracotta-400">{price}</span>
          <span className="inline-flex items-center gap-1.5 text-sm text-ink/80 group-hover:text-terracotta-400 transition-colors">İncele<ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" strokeWidth={1.5} /></span>
        </div>
      </div>
    </Link>
  );
}
